declare global {
    interface HTMLElementEventMap {
        "chart-changed": CustomEvent<number>
    }
}

export default class PowerChart extends HTMLElement {
    private canvas: HTMLCanvasElement;
    private imageBase: HTMLImageElement;
    private imageFill: HTMLImageElement;
    private imageOverlay: HTMLImageElement;
    public values: number[] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];

    private static readonly minRadius = 21;
    private static readonly maxRadius = 213;

    private pointerToSlice: Map<number, number[]> = new Map();

    constructor() {
        super();
        this.canvas = document.createElement("canvas") as HTMLCanvasElement;
        this.canvas.width = 512;
        this.canvas.height = 512;
        this.canvas.style.width = "100%";
        this.canvas.style.maxWidth = "512px";
        this.canvas.style.textAlign = "center";
        this.canvas.style.borderRadius = "45%"; // Slightly below 50% so "STRENGTH" isn't clipped
        this.canvas.style.touchAction = "pinch-zoom";

        this.canvas.addEventListener("pointerdown", e => {
            this.canvas.setPointerCapture(e.pointerId);
            this.handlePointerEvent(e);
        });
        this.canvas.addEventListener("pointermove", e => this.handlePointerEvent(e));
        this.canvas.addEventListener("pointerup", e => {
            this.pointerToSlice.delete(e.pointerId);
        });
        this.canvas.addEventListener("pointercancel", e => {
            const [sector, oldValue] = this.pointerToSlice.get(e.pointerId) ?? [-1, 0];
            if (sector == -1) return;
            this.setValue(sector, oldValue);
            this.pointerToSlice.delete(e.pointerId);
        });

        this.imageBase = document.createElement("img") as HTMLImageElement;
        this.imageBase.addEventListener("load", () => this.redraw());
        this.imageBase.src = require("./static/wheel_base.png");

        this.imageFill = document.createElement("img") as HTMLImageElement;
        this.imageFill.addEventListener("load", () => this.redraw());
        this.imageFill.src = require("./static/wheel_fill.png");

        this.imageOverlay = document.createElement("img") as HTMLImageElement;
        this.imageOverlay.addEventListener("load", () => this.redraw());
        this.imageOverlay.src = require("./static/wheel_overlay.png");

        const shadowRoot = this.attachShadow({ mode: 'open' });
        shadowRoot.appendChild(this.canvas);
    }

    private static map(current: number, in_min: number, in_max: number, out_min: number, out_max: number): number {
        return ((current - in_min) * (out_max - out_min)) / (in_max - in_min) + out_min;
    }

    private handlePointerEvent(event: PointerEvent) {
        if (event.buttons != 1) return;
        const scale = this.canvas.clientWidth / this.canvas.width;
        const centerX = this.canvas.clientWidth / 2;
        const centerY = this.canvas.clientHeight / 2;
        const minRadius = PowerChart.minRadius * scale;
        const maxRadius = PowerChart.maxRadius * scale;

        const mouseX = event.offsetX - centerX;
        const mouseY = event.offsetY - centerY;

        const angle = Math.atan2(mouseY, mouseX);
        const sector = Math.floor((angle / (Math.PI * 2) * this.values.length + 24) % 24);

        if (event.pointerType == "touch") {
            if (!this.pointerToSlice.has(event.pointerId))
                this.pointerToSlice.set(event.pointerId, [sector, this.getValue(sector)]);
            const [lockedSector, _] = this.pointerToSlice.get(event.pointerId) ?? [0, 0];

            const sectorAngle = lockedSector / 24 * Math.PI * 2;
            const dot = mouseX * Math.cos(sectorAngle) + mouseY * Math.sin(sectorAngle);
            const value = PowerChart.map(dot, minRadius, maxRadius, 0, 1);

            this.setValue(lockedSector, value);
        }
        else {
            let value = PowerChart.map(Math.hypot(mouseX, mouseY), minRadius, maxRadius, 0, 1);

            if (this.roundToTenth) {
                if (value < 0.05) value = 0;
                else value = Math.ceil(value * 10) / 10;
            }

            this.setValue(sector, value);
        }
    }

    private _roundToTenth: boolean = true;
    public get roundToTenth(): boolean {
        return this._roundToTenth;
    }
    public set roundToTenth(v: boolean) {
        this._roundToTenth = v;
        this.values.forEach((value, index) => this.setValue(index, value));
    }

    private coerceValue(value: number): number {
        if (!isFinite(value)) return 0;
        if (value > 1) value = 1;
        else if (value < 0) value = 0;
        const granularity = this.roundToTenth ? 0.1 : 0.01;
        return Math.round(value / granularity) * granularity;
    }

    public getValue(index: number): number {
        return this.values[index];
    }

    public setValue(index: number, value: number) {
        if (index < 0 || index >= 24) return;
        value = this.coerceValue(value);
        if (this.values[index] == value) return;
        this.values[index] = value;
        this.redraw();
        this.dispatchEvent(new CustomEvent<number>("chart-changed", { detail: index }));
    }

    public serialize() {
        return this.values.slice(24-7).concat(this.values.slice(0, 24-7))
            .map(n => (Math.floor(Math.max(0, Math.min(1, n)) * 100) / 10).toString())
            .join("-");
    }

    public deserialize(data: string) {
        try {
            let segments = data.split("-");
            segments.forEach((segment, i) => {
                let value = parseFloat(segment);
                if (value % 1 != 0) this.roundToTenth = false;
                this.setValue((i + 24-7) % 24, value / 10);
            });
        }
        finally {}
    }

    public redraw() {
        const ctx = this.canvas.getContext("2d");
        if (!ctx) return;
        ctx.save();
        ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        ctx.drawImage(this.imageBase, 0, 0);
        ctx.fillStyle = "#000000";

        const centerX = this.canvas.width / 2;
        const centerY = this.canvas.height / 2;
        const stops = this.values.length;
        const arc = Math.PI * 2 / stops;
        ctx.beginPath();
        for (let i = 0; i < stops; ++i) {
            const start = i * arc;
            const end = (i + 1) * arc;
            const radius = this.values[i] <= 0 ? 0 : PowerChart.minRadius + this.values[i] * (PowerChart.maxRadius - PowerChart.minRadius)
            ctx.arc(centerX, centerY, radius, start, end);
        }
        ctx.clip();
        ctx.drawImage(this.imageFill, 0, 0);
        ctx.restore();
        ctx.drawImage(this.imageOverlay, 0, 0);
    }

    public save() {
        let a = document.createElement("a") as HTMLAnchorElement;
        a.href = this.canvas.toDataURL();
        a.download = "powers.png";
        a.click();
        return true;
    }
}